/**
 * Install instructions for Settings -> Chat Widget -> Install. Pure functions
 * so the snippet an admin copies is covered by tests.
 */

export function buildEmbedSnippet(loaderUrl: string, publicKey: string, apiUrl?: string | null): string {
  const api = apiUrl ? ` data-api="${escapeAttribute(apiUrl)}"` : '';
  return `<script src="${escapeAttribute(loaderUrl)}" data-key="${escapeAttribute(publicKey)}"${api} async></script>`;
}

export interface PlatformGuide {
  id: string;
  name: string;
  steps: string[];
}

export const PLATFORM_GUIDES: PlatformGuide[] = [
  {
    id: 'any',
    name: 'Any website',
    steps: [
      'Copy the snippet above.',
      'Paste it just before the closing </body> tag on every page where the chat should appear.',
      'Publish the site and reload a page - the chat button appears in the corner.',
    ],
  },
  {
    id: 'wordpress',
    name: 'WordPress',
    steps: [
      'Install a header/footer plugin such as "WPCode" (or use your theme\'s custom code setting).',
      'Open its Footer section and paste the snippet.',
      'Save, then reload your site.',
    ],
  },
  {
    id: 'shopify',
    name: 'Shopify',
    steps: [
      'Go to Online Store -> Themes -> ... -> Edit code.',
      'Open layout/theme.liquid and paste the snippet just before </body>.',
      'Save, then reload your store.',
    ],
  },
  {
    id: 'wix',
    name: 'Wix',
    steps: [
      'Go to Settings -> Custom code -> + Add custom code.',
      'Paste the snippet, choose "All pages" and place it in "Body - end".',
      'Apply, then publish your site.',
    ],
  },
  {
    id: 'webflow',
    name: 'Webflow',
    steps: [
      'Open Site settings -> Custom code.',
      'Paste the snippet into "Footer code".',
      'Save and publish.',
    ],
  },
  {
    id: 'gtm',
    name: 'Google Tag Manager',
    steps: [
      'Create a new tag of type "Custom HTML" and paste the snippet.',
      'Set the trigger to "All Pages".',
      'Save, then Submit / Publish the container.',
    ],
  },
];

export function developerEmail(snippet: string, hostedChatUrl?: string): { subject: string; body: string } {
  const lines = [
    'Hi,',
    '',
    'Please add our support chat to the website by pasting this line just before the closing </body> tag on every page:',
    '',
    snippet,
    '',
    'Nothing else needs to be configured - appearance and behaviour are managed in CentraOne.',
  ];
  if (hostedChatUrl) {
    lines.push('', `For the mobile app or a link, the same chat is available at: ${hostedChatUrl}`);
  }
  lines.push('', 'Thanks!');
  return { subject: 'Please add our support chat to the website', body: lines.join('\n') };
}

export function mailtoLink(email: { subject: string; body: string }): string {
  return `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
